import React, { useCallback, useState } from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import favoriteItemsContext from '../contexts/favoriteItems';
import { readFavorites as readFavoritesApi, like as likeApi, dislike as dislikeApi } from '../api/providers';
import ProgressBackdrop from '../../preorder/components/ProgressBackdrop';
import { useAuthState } from '../../preorder/plugins/Auth';
import useAuth from '../../preorder/hooks/useAuth';

const { Provider } = favoriteItemsContext;

const typeMap = {
  LIKE: 'LIKE',
  DISLIKE: 'DISLIKE',
};

export default (WrappedComponent) => () => {
  const [checkAuthenticated] = useAuth();
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location || {};
  const { token } = useAuthState();

  const readFavorites = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return [];
    }

    try {
      setIsLoading(true);

      const favoritesResult = await readFavoritesApi(token);
      const { data, status } = favoritesResult || {};
      const { items } = data || {};

      if (status !== 'success') return [];

      setFavoriteItems(items);

      return items;
    } catch (e) {
      console.log('load favoriteItems failed');
    } finally {
      setIsLoading(false);
    }

    return [];
  }, [setIsLoading, token]);

  const toggleItems = useCallback(async (items) => {
    if (!checkAuthenticated()) {
      history.push('/register', { afterLoginUrl: pathname });
      return;
    }

    try {
      const itemsToLike = [];
      const itemsToDislike = [];
      const newFavoriteItems = cloneDeep(favoriteItems);

      items.forEach((item) => {
        const { itemType, itemId, type } = item || {};

        if (type === typeMap.LIKE) itemsToLike.push(item);
        if (type === typeMap.DISLIKE) itemsToDislike.push(item);

        const favoriteItemIndex = newFavoriteItems.findIndex((favItem) => {
          const { itemType: favItemItemType, itemId: favItemItemId } = favItem || {};

          return favItemItemType === itemType && favItemItemId === itemId;
        });

        if (favoriteItemIndex < 0) {
          newFavoriteItems.push({
            itemId,
            itemType,
            type,
          });
        } else {
          const favoriteItem = newFavoriteItems[favoriteItemIndex];
          const { type: favoriteItemType } = favoriteItem;

          if (favoriteItemType === type) {
            delete newFavoriteItems[favoriteItemIndex];
          } else {
            favoriteItem.type = type;
          }
        }
      });

      setFavoriteItems(newFavoriteItems.filter((i) => i));

      if (!isEmpty(itemsToLike)) {
        const likeResult = await likeApi({ token, items: itemsToLike });
        const { status } = likeResult || {};
        if (status !== 'success') throw new Error('execute like failed');
      }
      if (!isEmpty(itemsToDislike)) {
        const dislikeResult = await dislikeApi({ token, items: itemsToDislike });
        const { status } = dislikeResult || {};
        if (status !== 'success') throw new Error('execute dislike failed');
      }
    } catch (e) {
      // recover the favoriteItems if the API call fail
      console.log(e.mesage);
    }
  }, [favoriteItems, token, checkAuthenticated, history, pathname]);

  return (
    <Provider value={{
      favoriteItems,
      readFavorites,
      toggleItems,
    }}
    >
      <WrappedComponent />
      <ProgressBackdrop isShow={isLoading} />
    </Provider>
  );
};
